import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircleIcon, PlusIcon } from '@heroicons/react/24/outline';
import { api } from '../services/api';
import toast from 'react-hot-toast';
import { rebalanceTrafficSplit } from '../utils/rebalanceTrafficSplit';

const emptyForm = { name: '', notes: '', trackingUrl: '', trafficWeight: '0' };

const PHASE_LABELS = [
  ['initial', 'Initial'],
  ['phase_1', 'Phase 1'],
  ['phase_2', 'Phase 2'],
  ['phase_3', 'Phase 3'],
  ['phase_4', 'Phase 4'],
  ['phase_5', 'Phase 5'],
  ['phase_6', 'Phase 6'],
  ['phase_7', 'Phase 7'],
  ['cta', 'CTA']
];

const pct = (n) => (n == null ? '—' : `${n}%`);

const todayKey = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatRange = (from, to) => {
  if (!from) return '—';
  const a = new Date(from);
  const b = to ? new Date(to) : null;
  const fmt = (d) => d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  return b ? `${fmt(a)} → ${fmt(b)}` : `${fmt(a)} → now`;
};

const formatDay = (key) => {
  if (!key) return '—';
  const [y, m, d] = String(key).split('-').map(Number);
  if (!y || !m || !d) return key;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });
};

const ChatbotVersionsTab = ({ models }) => {
  const modelList = useMemo(() => (Array.isArray(models) ? models : []), [models]);
  const defaultModelId = useMemo(() => {
    const amy = modelList.find((m) => /amy/i.test(m.displayName || m.name || ''));
    return (amy || modelList[0])?._id || '';
  }, [modelList]);

  const [modelId, setModelId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [dayReleaseId, setDayReleaseId] = useState('');
  const [dayForm, setDayForm] = useState({ date: todayKey(), ofClicks: '', ofSubscribers: '' });
  const [savingDay, setSavingDay] = useState(false);
  const [splitDrafts, setSplitDrafts] = useState({});
  const [savingSplits, setSavingSplits] = useState(false);
  const [linkDrafts, setLinkDrafts] = useState({});
  const [photoDrafts, setPhotoDrafts] = useState({});
  const [targetDrafts, setTargetDrafts] = useState({});
  const [openPhotos, setOpenPhotos] = useState({});
  const [openTargets, setOpenTargets] = useState({});
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!modelId && defaultModelId) setModelId(defaultModelId);
  }, [defaultModelId, modelId]);

  const selectedModel = modelList.find((m) => m._id === modelId);

  const applyPayload = (payload) => {
    setData(payload);
    const nextSplits = {};
    const nextPhotos = {};
    const nextTargets = {};
    (payload.releases || []).forEach((r) => {
      nextSplits[r._id] = r.trafficWeight ?? 0;
      nextPhotos[r._id] = { ...(r.photoRates || {}) };
      nextTargets[r._id] = { ...(r.photoTargets || {}) };
    });
    setSplitDrafts(nextSplits);
    setPhotoDrafts(nextPhotos);
    setTargetDrafts(nextTargets);
  };

  const fetchReleases = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await api.get(`/chatbot-releases?modelId=${id}`);
      applyPayload(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load chatbot versions');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (modelId) fetchReleases(modelId);
  }, [modelId, fetchReleases]);

  useEffect(() => {
    const releases = data?.releases || [];
    if (!releases.length) return;
    if (!dayReleaseId || !releases.some((r) => r._id === dayReleaseId)) {
      const live = releases.find((r) => r.status === 'running') || releases[0];
      setDayReleaseId(live._id);
    }
  }, [data, dayReleaseId]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.trackingUrl.trim()) {
      toast.error('Name and tracking URL are required');
      return;
    }
    setCreating(true);
    try {
      const res = await api.post('/chatbot-releases', {
        modelId,
        name: form.name.trim(),
        notes: form.notes.trim(),
        trackingUrl: form.trackingUrl.trim(),
        trafficWeight: Number(form.trafficWeight) || 0,
        status: Number(form.trafficWeight) > 0 ? 'running' : 'paused'
      });
      applyPayload(res.data);
      setForm(emptyForm);
      setShowForm(false);
      toast.success(`Added ${res.data.release?.slug || 'version'} — set the split to send it traffic`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add version');
    } finally {
      setCreating(false);
    }
  };

  const handleSaveDay = async (e) => {
    e.preventDefault();
    if (!dayReleaseId) return;
    setSavingDay(true);
    try {
      const res = await api.patch(`/chatbot-releases/${dayReleaseId}`, {
        date: dayForm.date || todayKey(),
        ofClicks: Number(dayForm.ofClicks) || 0,
        ofSubscribers: Number(dayForm.ofSubscribers) || 0
      });
      applyPayload(res.data);
      toast.success(`Saved ${formatDay(dayForm.date || todayKey())}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save the day');
    } finally {
      setSavingDay(false);
    }
  };

  const handleSplitChange = (releaseId, rawNext) => {
    setSplitDrafts((d) => rebalanceTrafficSplit(d, releaseId, rawNext));
  };

  const handleSaveSplits = async () => {
    setSavingSplits(true);
    try {
      const weights = (data?.releases || []).map((r) => ({
        id: r._id,
        trafficWeight: Number(splitDrafts[r._id]) || 0
      }));
      const res = await api.put('/chatbot-releases/splits', { modelId, weights });
      applyPayload(res.data);
      toast.success('Traffic split saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save split');
    } finally {
      setSavingSplits(false);
    }
  };

  const handleAddLink = async (releaseId) => {
    const draft = linkDrafts[releaseId] || {};
    if (!draft.url) {
      toast.error('Paste a tracking URL');
      return;
    }
    setBusyId(releaseId);
    try {
      const res = await api.post(`/chatbot-releases/${releaseId}/links`, {
        url: draft.url.trim(),
        label: (draft.label || '').trim()
      });
      applyPayload(res.data);
      setLinkDrafts((d) => ({ ...d, [releaseId]: { url: '', label: '' } }));
      toast.success('Link added');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add link');
    } finally {
      setBusyId(null);
    }
  };

  const handleRemoveLink = async (releaseId, index) => {
    setBusyId(releaseId);
    try {
      const res = await api.delete(`/chatbot-releases/${releaseId}/links/${index}`);
      applyPayload(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove link');
    } finally {
      setBusyId(null);
    }
  };

  const handleSaveTargets = async (releaseId) => {
    setBusyId(releaseId);
    try {
      const res = await api.patch(`/chatbot-releases/${releaseId}`, {
        photoTargets: targetDrafts[releaseId] || {}
      });
      applyPayload(res.data);
      toast.success('Photo targets saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save photo targets');
    } finally {
      setBusyId(null);
    }
  };

  const handleSavePhotos = async (releaseId) => {
    setBusyId(releaseId);
    try {
      const res = await api.patch(`/chatbot-releases/${releaseId}`, {
        photoRates: photoDrafts[releaseId] || {}
      });
      applyPayload(res.data);
      toast.success('Photo rates saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save photo rates');
    } finally {
      setBusyId(null);
    }
  };

  const handlePause = async (releaseId) => {
    setBusyId(releaseId);
    try {
      const res = await api.post(`/chatbot-releases/${releaseId}/pause`);
      applyPayload(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to pause');
    } finally {
      setBusyId(null);
    }
  };

  const loadDay = (row) => {
    setDayForm({
      date: row.date,
      ofClicks: row.ofClicks ?? '',
      ofSubscribers: row.ofSubscribers ?? ''
    });
  };

  const prefillPrimary = () => {
    const url = data?.model?.primaryUrl || selectedModel?.ctaInfo?.fanvueLink || '';
    if (url) setForm((f) => ({ ...f, trackingUrl: url }));
  };

  const bestClick = data?.best?.bestClickRateId;
  const bestSub = data?.best?.bestSubRateId;
  const dayRelease = (data?.releases || []).find((r) => r._id === dayReleaseId);
  const loggedDays = dayRelease?.dailyStats || [];
  const splitTotal = (data?.releases || []).reduce((sum, r) => sum + (Number(splitDrafts[r._id]) || 0), 0);
  const modelDefaults = data?.model?.photoRates || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Chatbot versions</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
            The Model page is the chatbot. Here you A/B versions: tracking links, traffic split, and photo % per phase.
            New chats are frozen on one version. Log OF clicks and subs per day.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={modelId}
            onChange={(e) => setModelId(e.target.value)}
            className="form-select text-sm py-1.5"
          >
            {modelList.length === 0 && <option value="">No models</option>}
            {modelList.map((m) => (
              <option key={m._id} value={m._id}>{m.displayName || m.name}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => {
              setShowForm((v) => !v);
              if (!form.trackingUrl) prefillPrimary();
            }}
            className="btn btn-primary btn-sm"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            New version
          </button>
        </div>
      </div>

      {(data?.releases || []).length > 0 && (
        <div className="card">
          <div className="card-body space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="font-medium text-gray-900 dark:text-gray-100">Traffic split</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Always 100% of new chats. Dragging one slider takes from the others.
                  {splitTotal === 0 ? ' All at 0 — save after setting a split.' : ` Total ${splitTotal}.`}
                </p>
              </div>
              <button type="button" className="btn btn-primary btn-sm" disabled={savingSplits} onClick={handleSaveSplits}>
                {savingSplits ? 'Saving…' : 'Save split'}
              </button>
            </div>
            <div className="space-y-3">
              {(data?.releases || []).map((r) => (
                <div key={r._id} className="grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-12 sm:col-span-4">
                    <div className="font-medium text-gray-900 dark:text-gray-100 text-sm">{r.name}</div>
                    <div className="text-xs text-gray-500">{r.status} · {Number(splitDrafts[r._id]) || 0}% of new chats</div>
                  </div>
                  <div className="col-span-8 sm:col-span-6">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={Number(splitDrafts[r._id]) || 0}
                      onChange={(e) => handleSplitChange(r._id, e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <input
                      type="number"
                      min="0"
                      className="form-input w-full text-right py-1"
                      max={100}
                      value={splitDrafts[r._id] ?? 0}
                      onChange={(e) => handleSplitChange(r._id, e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <form onSubmit={handleCreate} className="card">
          <div className="card-body space-y-3">
            <h3 className="font-medium text-gray-900 dark:text-gray-100">Add a version</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Paste a unique OF tracking link. Set a starting weight if it should take traffic immediately; otherwise leave 0 and use the split sliders.
            </p>
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="block">
                <span className="text-xs text-gray-500 dark:text-gray-400">Name</span>
                <input
                  className="form-input mt-1 w-full"
                  placeholder="e.g. V3"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                {/^v3\b/i.test(form.name.trim()) && (
                  <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                    V3 applies the intent-first overlay, early photos, short bubbles, and a 100-exchange cap.
                  </span>
                )}
              </label>
              <label className="block">
                <span className="text-xs text-gray-500 dark:text-gray-400">Tracking URL</span>
                <input
                  className="form-input mt-1 w-full"
                  placeholder="https://onlyfans.com/…"
                  value={form.trackingUrl}
                  onChange={(e) => setForm({ ...form, trackingUrl: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-500 dark:text-gray-400">Starting weight</span>
                <input
                  type="number"
                  min="0"
                  className="form-input mt-1 w-full"
                  value={form.trafficWeight}
                  onChange={(e) => setForm({ ...form, trafficWeight: e.target.value })}
                />
              </label>
            </div>
            <label className="block">
              <span className="text-xs text-gray-500 dark:text-gray-400">What changed (optional)</span>
              <textarea
                className="form-input mt-1 w-full"
                rows={2}
                placeholder="Higher phase-1 photo rate, new OF campaign link…"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </label>
            <div className="flex gap-2">
              <button type="submit" disabled={creating} className="btn btn-primary btn-sm">
                {creating ? 'Adding…' : 'Add version'}
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}

      {(data?.releases || []).map((r) => {
        const links = r.trackingLinks?.length ? r.trackingLinks : (r.trackingUrl ? [{ url: r.trackingUrl, label: 'Primary' }] : []);
        const linkDraft = linkDrafts[r._id] || { url: '', label: '' };
        const photos = photoDrafts[r._id] || {};
        return (
          <div key={`detail-${r._id}`} className="card">
            <div className="card-body space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-gray-900 dark:text-gray-100">{r.name}</h3>
                    {r.status === 'running' && Number(r.trafficWeight) > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-xs text-emerald-700 dark:text-emerald-300">
                        <CheckCircleIcon className="h-3.5 w-3.5" /> {r.trafficShare || 0}%
                      </span>
                    )}
                    <span className="text-xs text-gray-500">{r.status}</span>
                    {r.behavior?.conversionOverlay && (
                      <span className="text-xs text-amber-700 dark:text-amber-300">conversion overlay</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">{r.slug}</div>
                  {r.behavior?.conversionOverlay && (
                    <p className="text-xs text-gray-500 mt-1 max-w-xl">
                      New chats on this version get fixed pics per phase (only when the chat can carry one), no “You here???” pings, contextual fallbacks, honor OF refusals, and hold the thread while he is still talking.
                    </p>
                  )}
                </div>
                {r.status === 'running' && (
                  <button type="button" className="btn btn-secondary btn-sm" disabled={busyId === r._id} onClick={() => handlePause(r._id)}>
                    Pause
                  </button>
                )}
              </div>

              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Tracking links</p>
                <ul className="space-y-1 text-sm">
                  {links.map((link, i) => (
                    <li key={`${link.url}-${i}`} className="flex items-center justify-between gap-2">
                      <a href={link.url} target="_blank" rel="noreferrer" className="text-primary-600 dark:text-primary-400 break-all hover:underline">
                        {link.label ? `${link.label} — ` : ''}{link.url}
                      </a>
                      {links.length > 1 && (
                        <button
                          type="button"
                          className="text-xs text-red-500 hover:underline"
                          disabled={busyId === r._id}
                          onClick={() => handleRemoveLink(r._id, i)}
                        >
                          Remove
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="mt-2 flex flex-wrap gap-2">
                  <input
                    className="form-input flex-1 min-w-[12rem]"
                    placeholder="https://onlyfans.com/… extra campaign"
                    value={linkDraft.url}
                    onChange={(e) => setLinkDrafts((d) => ({ ...d, [r._id]: { ...linkDraft, url: e.target.value } }))}
                  />
                  <input
                    className="form-input w-32"
                    placeholder="Label"
                    value={linkDraft.label}
                    onChange={(e) => setLinkDrafts((d) => ({ ...d, [r._id]: { ...linkDraft, label: e.target.value } }))}
                  />
                  <button type="button" className="btn btn-secondary btn-sm" disabled={busyId === r._id} onClick={() => handleAddLink(r._id)}>
                    Add link
                  </button>
                </div>
              </div>

              <div>
                <button
                  type="button"
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  onClick={() => setOpenTargets((o) => ({ ...o, [r._id]: !o[r._id] }))}
                >
                  {openTargets[r._id] ? 'Hide photo targets' : 'Fixed pics per phase'}
                </button>
                {openTargets[r._id] && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs text-gray-500">
                      Max pics in that phase. Empty = use % rates. 0 = none. Only sent when the chat can carry a selfie; the reply is written to match.
                    </p>
                    <div className="grid gap-2 sm:grid-cols-3">
                      {PHASE_LABELS.map(([key, label]) => (
                        <label key={`t-${key}`} className="block text-xs">
                          <span className="text-gray-500">{label}</span>
                          <input
                            type="number"
                            min="0"
                            max="10"
                            className="form-input mt-1 w-full"
                            placeholder="—"
                            value={(targetDrafts[r._id] || {})[key] ?? ''}
                            onChange={(e) => setTargetDrafts((d) => ({
                              ...d,
                              [r._id]: { ...(d[r._id] || {}), [key]: e.target.value === '' ? '' : e.target.value }
                            }))}
                          />
                        </label>
                      ))}
                    </div>
                    <button type="button" className="btn btn-secondary btn-sm" disabled={busyId === r._id} onClick={() => handleSaveTargets(r._id)}>
                      Save photo targets
                    </button>
                  </div>
                )}
              </div>

              <div>
                <button
                  type="button"
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  onClick={() => setOpenPhotos((o) => ({ ...o, [r._id]: !o[r._id] }))}
                >
                  {openPhotos[r._id] ? 'Hide photo rates' : 'Photo rates per phase'}
                </button>
                {openPhotos[r._id] && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs text-gray-500">Empty = Model default. Saved values freeze on new chats assigned to this version.</p>
                    <div className="grid gap-2 sm:grid-cols-3">
                      {PHASE_LABELS.map(([key, label]) => (
                        <label key={key} className="block text-xs">
                          <span className="text-gray-500">
                            {label}
                            {modelDefaults[key] != null ? ` (model ${modelDefaults[key]}%)` : ''}
                          </span>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            className="form-input mt-1 w-full"
                            placeholder={modelDefaults[key] != null ? String(modelDefaults[key]) : 'default'}
                            value={photos[key] ?? ''}
                            onChange={(e) => setPhotoDrafts((d) => ({
                              ...d,
                              [r._id]: { ...photos, [key]: e.target.value === '' ? '' : e.target.value }
                            }))}
                          />
                        </label>
                      ))}
                    </div>
                    <button type="button" className="btn btn-secondary btn-sm" disabled={busyId === r._id} onClick={() => handleSavePhotos(r._id)}>
                      Save photo rates
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {(data?.releases || []).length > 0 && (
        <form onSubmit={handleSaveDay} className="card">
          <div className="card-body space-y-4">
            <div>
              <h3 className="font-medium text-gray-900 dark:text-gray-100">Log OF numbers for a day</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Open the tracking link in OnlyFans, copy that day’s clicks and subscribers, save here. Totals on the version are the sum of these days.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <label className="block lg:col-span-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">Version / link</span>
                <select
                  className="form-select mt-1 w-full"
                  value={dayReleaseId}
                  onChange={(e) => {
                    setDayReleaseId(e.target.value);
                    setDayForm({ date: todayKey(), ofClicks: '', ofSubscribers: '' });
                  }}
                >
                  {(data?.releases || []).map((r) => (
                    <option key={r._id} value={r._id}>
                      {r.name}{r.status === 'running' ? ' (running)' : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-xs text-gray-500 dark:text-gray-400">Day</span>
                <input
                  type="date"
                  className="form-input mt-1 w-full"
                  value={dayForm.date}
                  onChange={(e) => setDayForm({ ...dayForm, date: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-500 dark:text-gray-400">OF clicks</span>
                <input
                  type="number"
                  min="0"
                  className="form-input mt-1 w-full"
                  placeholder="0"
                  value={dayForm.ofClicks}
                  onChange={(e) => setDayForm({ ...dayForm, ofClicks: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-500 dark:text-gray-400">OF subscribers</span>
                <input
                  type="number"
                  min="0"
                  className="form-input mt-1 w-full"
                  placeholder="0"
                  value={dayForm.ofSubscribers}
                  onChange={(e) => setDayForm({ ...dayForm, ofSubscribers: e.target.value })}
                />
              </label>
            </div>
            <button type="submit" disabled={savingDay || !dayReleaseId} className="btn btn-primary btn-sm">
              {savingDay ? 'Saving…' : 'Save day'}
            </button>

            {loggedDays.length > 0 && (
              <div className="overflow-x-auto border-t border-gray-200 dark:border-gray-700 pt-3">
                <table className="min-w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <tr>
                      <th className="py-1.5 pr-4 font-medium">Day</th>
                      <th className="py-1.5 pr-4 font-medium text-right">Chats</th>
                      <th className="py-1.5 pr-4 font-medium text-right">Links</th>
                      <th className="py-1.5 pr-4 font-medium text-right">Clicks</th>
                      <th className="py-1.5 pr-4 font-medium text-right">Subs</th>
                      <th className="py-1.5 font-medium" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {loggedDays.map((row) => (
                      <tr key={row.date} className={row.date === dayForm.date ? 'bg-primary-50/60 dark:bg-primary-900/10' : ''}>
                        <td className="py-2 pr-4 text-gray-800 dark:text-gray-200">{formatDay(row.date)}</td>
                        <td className="py-2 pr-4 text-right tabular-nums text-gray-500">{row.conversations || 0}</td>
                        <td className="py-2 pr-4 text-right tabular-nums text-gray-500">{row.linksSent || 0}</td>
                        <td className="py-2 pr-4 text-right tabular-nums">{row.ofClicks}</td>
                        <td className="py-2 pr-4 text-right tabular-nums">{row.ofSubscribers}</td>
                        <td className="py-2">
                          <button
                            type="button"
                            className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                            onClick={() => loadDay(row)}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </form>
      )}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/60 text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-2 font-medium">Version</th>
                <th className="px-4 py-2 font-medium">Split</th>
                <th className="px-4 py-2 font-medium">Period</th>
                <th className="px-4 py-2 font-medium text-right">Chats</th>
                <th className="px-4 py-2 font-medium text-right">Links sent</th>
                <th className="px-4 py-2 font-medium text-right">OF clicks</th>
                <th className="px-4 py-2 font-medium text-right">OF subs</th>
                <th className="px-4 py-2 font-medium text-right">Click / link</th>
                <th className="px-4 py-2 font-medium text-right">Sub / click</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading && (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500">Loading…</td>
                </tr>
              )}
              {!loading && (data?.releases || []).length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                    No versions yet. Add one with a unique OF tracking link.
                  </td>
                </tr>
              )}
              {(data?.releases || []).map((r) => (
                <tr key={r._id} className={r.status === 'running' && r.trafficWeight > 0 ? 'bg-emerald-50/50 dark:bg-emerald-900/10' : ''}>
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100">{r.name}</span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{r.slug}</div>
                  </td>
                  <td className="px-4 py-3 text-sm">{r.trafficShare || 0}%</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    {formatRange(r.activatedAt, r.retiredAt)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{r.conversations.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {r.linksSent.toLocaleString()}
                    <div className="text-xs text-gray-400">{pct(r.rates.linkRate)} of chats</div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {r.ofClicks.toLocaleString()}
                    <div className="text-xs text-gray-400">{(r.dailyStats || []).length} day{(r.dailyStats || []).length === 1 ? '' : 's'}</div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{r.ofSubscribers.toLocaleString()}</td>
                  <td className={`px-4 py-3 text-right tabular-nums ${bestClick === r._id ? 'font-semibold text-emerald-600 dark:text-emerald-400' : ''}`}>
                    {pct(r.rates.clickRate)}
                    {bestClick === r._id && <div className="text-xs">best click</div>}
                  </td>
                  <td className={`px-4 py-3 text-right tabular-nums ${bestSub === r._id ? 'font-semibold text-emerald-600 dark:text-emerald-400' : ''}`}>
                    {pct(r.rates.subGivenClick)}
                    {bestSub === r._id && <div className="text-xs">best sub</div>}
                  </td>
                </tr>
              ))}
              {!loading && data?.unversioned && (data.unversioned.conversations > 0) && (
                <tr className="text-gray-400 dark:text-gray-500">
                  <td className="px-4 py-3">
                    <span className="font-medium">History</span>
                    <div className="text-xs">Before versioning — not in the live comparison</div>
                  </td>
                  <td className="px-4 py-3">—</td>
                  <td className="px-4 py-3">—</td>
                  <td className="px-4 py-3 text-right tabular-nums">{data.unversioned.conversations.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{data.unversioned.linksSent.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">—</td>
                  <td className="px-4 py-3 text-right">—</td>
                  <td className="px-4 py-3 text-right">—</td>
                  <td className="px-4 py-3 text-right">—</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ChatbotVersionsTab;
